/**
 * AI Coach Chat Component - Simplified
 */

import { ArrowDown, RotateCcw, Send, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useWorkoutData } from '../../api/hooks/useWorkoutData'
import { useCoachChat } from '../../hooks/useCoachChat'
import { selectIsConfigured, useOpenAIStore } from '../../stores/openAIStore'
import Milo from '../icons/milo'
import { Card } from '../ui/card'
import { Dropdown } from '../ui/dropdown'
import { LoadingSpinner } from '../ui/loading-spinner'
import { Textarea } from '../ui/textarea'
import { UnifiedButton } from '../ui/unified-button'
import { CoachMessage } from './CoachMessage'
import { QuotaExhaustedOverlay } from './QuotaExhaustedOverlay'

// Animated dots component
const AnimatedDots = () => {
  const [dots, setDots] = useState('.')

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => {
        if (prev === '.') return '..'
        if (prev === '..') return '...'
        return '.'
      })
    }, 500)

    return () => clearInterval(interval)
  }, [])

  return <span>{dots}</span>
}

interface CoachChatProps {
  isOpen?: boolean
  onClose?: () => void
}

export const CoachChat = ({ isOpen, onClose }: CoachChatProps) => {
  const isConfigured = useOpenAIStore(selectIsConfigured)
  const quotaExhausted = useOpenAIStore((state) => state.quotaExhausted)
  const setQuotaExhausted = useOpenAIStore((state) => state.setQuotaExhausted)
  const workoutData = useWorkoutData()
  const dataLoading = workoutData.isLoading

  const {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    isLoading,
    toolStatus,
    clearMessages,
    stop,
    isReady,
    setModel,
    currentModel
  } = useCoachChat()

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [isAtBottom, setIsAtBottom] = useState(true)
  const [hasNewMessages, setHasNewMessages] = useState(false)
  const lastMessageRef = useRef<string>('')

  // Model options for dropdown
  const modelOptions = [
    { value: 'gpt-4.1', label: 'GPT-4.1' },
    { value: 'o1', label: 'o1' }
  ]

  // Update model when selection changes
  const handleModelChange = useCallback(
    (model: string) => {
      if (setModel) {
        setModel(model as 'gpt-4.1' | 'o1')
      }
    },
    [setModel]
  )

  // Check if user is at bottom of scroll
  const checkIfAtBottom = useCallback(() => {
    if (!scrollContainerRef.current) return

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
    // Consider "at bottom" if within 100px of the bottom to account for smooth scrolling
    // Larger threshold during loading to maintain auto-scroll during streaming
    const threshold = isLoading ? 150 : 100
    const atBottom = scrollHeight - scrollTop - clientHeight <= threshold
    setIsAtBottom(atBottom)

    // Clear new messages indicator when user scrolls to bottom
    if (atBottom && hasNewMessages) {
      setHasNewMessages(false)
    }
  }, [hasNewMessages, isLoading])

  // Auto-scroll during streaming and when new messages arrive
  useEffect(() => {
    const lastMessage = messages[messages.length - 1]
    const lastMessageContent = lastMessage?.content || ''

    // Check if content is being streamed (content is growing)
    const isStreaming =
      lastMessage?.role === 'assistant' &&
      lastMessageContent !== lastMessageRef.current &&
      isLoading

    if (isAtBottom && (isStreaming || lastMessageContent !== lastMessageRef.current)) {
      // Smooth scroll during streaming, instant for complete messages
      const behavior = isStreaming ? 'smooth' : 'auto'
      messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' })
      setHasNewMessages(false)
    } else if (!isAtBottom && lastMessage?.role === 'assistant' && !isStreaming) {
      // User is scrolled up and new assistant message completed
      setHasNewMessages(true)
    }

    // Update last message reference
    lastMessageRef.current = lastMessageContent
  }, [messages, isAtBottom, isLoading])

  // Set up scroll listener with passive option for better performance
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return

    // Check initial state
    checkIfAtBottom()

    scrollContainer.addEventListener('scroll', checkIfAtBottom, { passive: true })
    return () => scrollContainer.removeEventListener('scroll', checkIfAtBottom)
  }, [checkIfAtBottom])

  // Simple focus when sidebar opens and scroll to bottom
  useEffect(() => {
    if (isOpen && isReady) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        inputRef.current?.focus()
        // Scroll to bottom when opening
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' })
      })
    }
  }, [isOpen, isReady])

  // Auto-focus textarea after assistant finishes streaming
  useEffect(() => {
    // Focus when isLoading changes from true to false
    if (!isLoading && isOpen && isReady) {
      // Small delay to ensure the message has fully rendered
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    }
  }, [isLoading, isOpen, isReady])

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Shift+Escape - focus input
      if (e.key === 'Escape' && e.shiftKey) {
        e.preventDefault()
        e.stopPropagation() // Prevent event from bubbling to AppViewport
        e.stopImmediatePropagation() // Stop other handlers

        // Force focus with a small delay to break out of modal focus trap
        setTimeout(() => {
          inputRef.current?.focus()
          inputRef.current?.select()
        }, 0)
      }
    }

    // Add to window with highest priority
    window.addEventListener('keydown', handleKeyDown, { capture: true })
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true })
  }, [isOpen])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    handleSubmit(e)
  }

  // Wrapper to handle textarea change events
  const handleTextareaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      // Create a minimal synthetic event that handleInputChange expects
      const syntheticEvent = {
        target: { value: e.target.value }
      } as React.ChangeEvent<HTMLInputElement>
      handleInputChange(syntheticEvent)
    },
    [handleInputChange]
  )

  // Show message if API key not configured
  if (!isConfigured) {
    return (
      <Card className="flex h-full w-full flex-col rounded-none border-0 p-0">
        {/* Header with close button */}
        <div className="border-b p-4 py-2">
          <div className="flex items-center justify-between">
            {/* Empty left side for alignment */}
            <div />

            {/* Close button - visible on all devices */}
            <UnifiedButton variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
              <div className="flex size-full items-center justify-center">
                <X className="h-4 w-4" />
              </div>
            </UnifiedButton>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-1 items-center justify-center p-8">
          <div className="max-w-md space-y-4 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900">
              <Milo className="h-8 w-8 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">API Key Required</h3>
              <p className="text-muted-foreground text-sm">
                Configure your OpenAI API key in Settings to start using Milo (AI Coach).
              </p>
            </div>
          </div>
        </div>
      </Card>
    )
  }

  // Show loading while data loads
  if (dataLoading) {
    return (
      <Card className="flex h-full w-full items-center justify-center rounded-none border-0">
        <div className="space-y-2 text-center">
          <LoadingSpinner size="lg" />
        </div>
      </Card>
    )
  }

  // Show message if no workout data
  if (!workoutData || !workoutData.workouts?.length) {
    return (
      <Card className="flex h-full w-full items-center justify-center rounded-none border-0 p-8">
        <div className="max-w-md space-y-2 text-center">
          <h3 className="text-lg font-semibold">No Workout Data</h3>
          <p className="text-muted-foreground text-sm">
            The AI Coach needs your workout data to provide insights. Please sync your data in
            Settings → Data Source.
          </p>
        </div>
      </Card>
    )
  }

  return (
    <Card className="relative flex h-full w-full flex-col overflow-hidden rounded-none border-0 p-0">
      {/* Header */}
      <div className="border-b p-4 py-2">
        <div className="flex items-center justify-between">
          {/* Left side - Model selector and Clear button */}
          <div className="flex items-center gap-2">
            {/* Model Selector Dropdown */}
            <Dropdown
              options={modelOptions}
              value={currentModel}
              onValueChange={handleModelChange}
              className="w-28"
              variant="short"
            />

            {/* Clear button - only show when there are messages */}
            {messages.length > 0 && (
              <UnifiedButton variant="ghost" size="sm" onClick={clearMessages}>
                <div className="flex items-center gap-2">
                  <RotateCcw className="h-4 w-4" />
                  <span className="hidden md:inline">Clear</span>
                </div>
              </UnifiedButton>
            )}
          </div>

          {/* Close button - visible on all devices */}
          <UnifiedButton variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
            <div className="flex size-full items-center justify-center">
              <X className="h-4 w-4" />
            </div>
          </UnifiedButton>
        </div>
      </div>

      {/* Messages */}
      <div className="relative flex-1 overflow-hidden">
        <div ref={scrollContainerRef} className="absolute inset-0 overflow-y-auto">
          {messages.length === 0 ? (
            <div className="flex h-full items-center justify-center px-4">
              <div className="max-w-md space-y-4 text-center">
                <div className="space-y-2">
                  <div className="flex flex-col items-center gap-2">
                    <div className="bg-muted flex size-16 items-center justify-center rounded-full">
                      <Milo className="h-8 w-8 translate-x-[3px] -translate-y-[3px]" gradient />
                    </div>
                    <h3 className="text-lg font-semibold">Milo</h3>
                  </div>

                  <p className="text-muted-foreground text-sm">
                    Ask me anything about your training! I can analyze your workouts, check your
                    progress, and provide personalized recommendations.
                  </p>
                  <p className="text-muted-foreground text-xs">
                    Try: &quot;How was my last workout?&quot; or &quot;Am I training too much?&quot;
                  </p>
                </div>

                {/* Keyboard shortcuts */}
                <div className="text-muted-foreground mt-6 space-y-1.5 text-xs">
                  <div
                    className="text-muted-foreground/60 mb-2 text-center tracking-wider uppercase"
                    style={{ fontSize: '10px' }}
                  >
                    Keyboard Shortcuts
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <kbd className="bg-muted/50 border-muted-foreground/20 rounded border px-1.5 py-0.5 font-mono text-xs">
                      {navigator.platform.includes('Mac') ? '⌘S' : 'Ctrl+S'}
                    </kbd>
                    <span className="text-muted-foreground/80">Toggle Milo</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <kbd className="bg-muted/50 border-muted-foreground/20 rounded border px-1.5 py-0.5 font-mono text-xs">
                      Esc
                    </kbd>
                    <span className="text-muted-foreground/80">Close Milo</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <kbd className="bg-muted/50 border-muted-foreground/20 rounded border px-1.5 py-0.5 font-mono text-xs">
                      Shift+Esc
                    </kbd>
                    <span className="text-muted-foreground/80">Focus Input</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4 p-4">
              {messages.map((message) => {
                // Don't show empty assistant message loading dots if we have a tool status
                if (
                  message.role === 'assistant' &&
                  message.content === '' &&
                  !message.tool_calls &&
                  toolStatus
                ) {
                  return null
                }
                return <CoachMessage key={message.id} message={message} />
              })}

              {/* Tool status indicator */}
              {toolStatus && (
                <div className="text-muted-foreground pl-4 text-sm">
                  {toolStatus}
                  <AnimatedDots />
                </div>
              )}

              {/* Add breathing room at the bottom */}
              <div className="h-12" />
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Scroll to bottom button */}
        {hasNewMessages && messages.length > 0 && (
          <button
            onClick={() => {
              messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
              setHasNewMessages(false)
            }}
            className="bg-foreground text-background absolute bottom-4 left-1/2 flex -translate-x-1/2 cursor-pointer items-center gap-2 rounded-full px-3 py-2 shadow-lg transition-all"
          >
            <ArrowDown className="h-3 w-3" />
            <span className="text-xs font-medium">New messages</span>
          </button>
        )}
      </div>

      {/* Input */}
      <form onSubmit={onSubmit} className="px-2 pt-0 pb-2">
        <div className="relative">
          <Textarea
            ref={inputRef}
            value={input}
            onChange={handleTextareaChange}
            placeholder={
              quotaExhausted
                ? 'API credits exhausted - add credits to continue'
                : 'Ask about your training...'
            }
            disabled={isLoading || !isReady || quotaExhausted}
            className="max-h-[120px] min-h-[40px] w-full resize-none rounded-xl px-4 py-4 pr-12 md:text-sm"
            onKeyDown={(e) => {
              // Submit on Enter (without shift)
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                if (input.trim() && isReady && !isLoading) {
                  const form = e.currentTarget.closest('form')
                  if (form) {
                    form.requestSubmit()
                  }
                }
              }
            }}
          />
          {/* Send/Stop button - only visible when there's input or loading */}
          {(input.trim() || isLoading) && (
            <div className="absolute top-2 right-1.5">
              {isLoading ? (
                <UnifiedButton
                  type="button"
                  onClick={() => stop()}
                  className="size-10 rounded-full bg-black p-0 text-white hover:bg-white/90"
                >
                  <div className="flex size-full items-center justify-center">
                    <span className="text-xs font-medium">Stop</span>
                  </div>
                </UnifiedButton>
              ) : (
                <UnifiedButton
                  type="submit"
                  disabled={!input.trim() || !isReady || quotaExhausted}
                  className="size-10 rounded-full bg-white p-0 text-black hover:bg-white/90"
                >
                  <div className="flex size-full items-center justify-center">
                    <Send className="h-4 w-4" />
                  </div>
                </UnifiedButton>
              )}
            </div>
          )}
        </div>
      </form>

      {/* Quota Exhausted Overlay */}
      <QuotaExhaustedOverlay
        isVisible={quotaExhausted}
        onDismiss={() => setQuotaExhausted(false)}
      />
    </Card>
  )
}
